/**
 * English copy catalog for the `community` feature (forums, threads, Hall of
 * Legends). Keys mirror the `t('community.*')` calls in
 * packages/@gamecodex/world-ui/src/features/community.
 */
export const communityEn = {
  // CommunityPage
  'community.page.seoTitle': 'Community',
  'community.page.seoDescription':
    'Join the Grymwyrd community: forums, lore theories, GM tools, homebrew, and the Hall of Legends.',
  'community.page.eyebrow': 'The Gathering Dark',
  'community.page.title': 'Community',
  'community.page.intro':
    'Trade tactics, spin theories, share homebrew, and rise toward canon. Every post you make earns coins and reputation in the eternal twilight.',
  'community.page.loading': 'Gathering the faithful…',
  'community.page.forumCategories': 'Forum Categories',
  'community.page.categoryStats': '{threads} threads · {posts} posts',
  'community.page.recentActivity': 'Recent Activity',
  'community.page.liveFeed': 'live feed',
  'community.page.recentEmpty':
    'The forums are silent tonight. Start a discussion to wake the dead.',
  'community.page.hallOfLegends': 'Hall of Legends',
  'community.page.hallBlurb': 'The greatest contributors and the characters they forged into canon.',
  'community.page.enterTheHall': 'Enter the Hall',
  'community.page.playerSpotlights': 'Player Spotlights',
  'community.page.summoningLegends': 'Summoning legends…',
  'community.page.sampleNotice': 'Showing sample community content while the live forums awaken.',

  // FlagButton
  'community.flag.errorQueued':
    'We could not reach the moderators just now, but your report has been queued.',
  'community.flag.ariaLabel': 'Flag this post for moderation',
  'community.flag.label': 'Flag',
  'community.flag.modalTitle': 'Report to the Keepers',
  'community.flag.successDelivered':
    'Your report has been delivered to the moderators. The Keepers thank you.',
  'community.flag.close': 'Close',
  'community.flag.signInNotice':
    'You can still describe the issue, but you must be signed in for the report to reach the moderators.',
  'community.flag.whyFlagging': 'Why are you flagging this?',
  'community.flag.reason.spamOrAdvertising': 'Spam or advertising',
  'community.flag.reason.harassmentOrAbuse': 'Harassment or abuse',
  'community.flag.reason.spoilersWithoutWarning': 'Spoilers without warning',
  'community.flag.reason.offTopic': 'Off-topic',
  'community.flag.reason.otherDescribeBelow': 'Other (describe below)',
  'community.flag.describeIssue': 'Describe the issue',
  'community.flag.detailPlaceholder': 'Tell the Keepers what is wrong…',
  'community.flag.cancel': 'Cancel',
  'community.flag.reporting': 'Reporting…',
  'community.flag.submitReport': 'Submit report',

  // ForumCategoryPage
  'community.category.seoTitle': '{name} — Forums',
  'community.category.seoDescription': 'Discussions in the {name} forum on Grymwyrd.',
  'community.category.breadcrumb': 'Community',
  'community.category.entityTiedPrefix': 'Entity-tied board · threads may link to',
  'community.category.encyclopediaEntries': 'encyclopedia entries',
  'community.category.sampleNotice': 'Showing sample threads while the live forum awakens.',

  // HallOfLegendsPage
  'community.legends.seoTitle': 'Hall of Legends',
  'community.legends.seoDescription':
    'The greatest contributors to Grymwyrd and the characters they forged into canon.',
  'community.legends.breadcrumb': 'Community',
  'community.legends.title': 'Hall of Legends',
  'community.legends.intro':
    'Here stand the architects of the eternal twilight — the contributors whose words became law, and the characters so beloved they were carved into canon.',
  'community.legends.loading': 'Lighting the candles…',
  'community.legends.topContributors': 'Top Contributors',
  'community.legends.noContributors':
    'No legends have risen yet. Post, theorize, and create to claim your place.',
  'community.legends.canonizedCharacters': 'Canonized Characters',
  'community.legends.noCharacters':
    'No characters have been canonized yet. The bestiary awaits its next horror.',
  'community.legends.sampleNotice': 'Showing sample legends while the live hall is consecrated.',

  // NestedReplies
  'community.replies.replyBy': 'Reply by {author}',
  'community.replies.reply': 'Reply',
  'community.replies.replyToPlaceholder': 'Reply to {author}…',
  'community.replies.unlike': 'Unlike this post',
  'community.replies.like': 'Like this post',

  // PlayerSpotlight
  'community.spotlight.rep': '{rep} rep',
  'community.spotlight.viewInEncyclopedia': 'View {name} in the encyclopedia',

  // PostComposer
  'community.composer.placeholder': 'Add your voice to the dark…',
  'community.composer.signInPrompt': 'Sign in to join the discussion and earn coins for your words.',
  'community.composer.signInToReply': 'Sign in to reply',
  'community.composer.youFallback': 'You',
  'community.composer.saveError':
    'Your reply could not be saved to the server yet — it is shown locally only.',
  'community.composer.writeReply': 'Write a reply',
  'community.composer.writePost': 'Write a post',
  'community.composer.replyText': 'Reply text',
  'community.composer.postText': 'Post text',
  'community.composer.posting': 'Posting…',
  'community.composer.postReply': 'Post reply',
  'community.composer.post': 'Post',
  'community.composer.cancel': 'Cancel',
  'community.composer.coinsPerPost': '+{count} coins per post',
  'community.composer.coinsEarned': '+{count} coins earned for your post!',

  // ThreadItem
  'community.thread.pinned': 'Pinned thread',
  'community.thread.byPrefix': 'by',
  'community.thread.repliesTitle': '{count} replies',
  'community.thread.likesTitle': '{count} likes',
  'community.thread.viewsTitle': '{count} views',
  'community.thread.entityEntry': '{type} entry',

  // ThreadList
  'community.threadList.empty':
    'No discussions have stirred here yet. Be the first to break the silence.',

  // ThreadPage
  'community.threadPage.loading': 'Unsealing the thread…',
  'community.threadPage.seoDescription': 'A discussion in the Grymwyrd community forums.',
  'community.threadPage.replyCount': '{count} replies',
  'community.threadPage.opened': 'opened {time}',
  'community.threadPage.entityEntry': '{type} entry',
  'community.threadPage.sampleNotice': 'Showing sample discussion while the live forum awakens.',
  'community.threadPage.openingPostBy': 'Opening post by {author}',
  'community.threadPage.repliesHeading': 'Replies',
  'community.threadPage.noReplies': 'No replies yet. Be the first to answer the call.',
  'community.threadPage.addYourReply': 'Add your reply',
} as const
